import {
  HOME,
  SSO_REDIRECT,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import {
  IStyles,
  InputFieldContainer,
  LinkButton,
  OTP_LENGTH,
  OtpInputs,
  PrimaryButton,
  isValidEmail,
  isValidMobileNumber,
  pxToRem,
  pxTovW,
} from '@geneo2-web/shared-ui';
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from '@react-oauth/google';
import { Box, Typography } from '@mui/material';
import { useAuthentication } from 'apps/student/src/utils/customHooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { googleOauthLoginApi } from '../../Pal/api/sso-login';

const styles: IStyles = {
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    justifyContent: { xs: 'flex-start', md: 'center' },
    alignItems: 'center',
  },
  containerTop: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxTovW(41) },
  },
  containerBottom: {
    marginTop: { xs: pxToRem(75), md: pxTovW(59) },
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xs: pxToRem(25), md: pxTovW(30) },
  },
};

const ClimateCourseLogin = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthentication();
  const [loginType, setLoginType] = useState<'email' | 'phone'>('email');
  const [credential, setCredential] = useState('');
  const [loginStep, setLoginStep] = useState<'login' | 'otp' | 'user-details'>(
    'login'
  );
  const [otp, setOtp] = useState('');
  const [seconds, setSeconds] = useState(30);
  const [credentialError, setCredentialError] = useState('');
  const [otpStepError, setOtpStepError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const recaptchaSiteKey = process.env.NX_RECAPTCHA_SITE_KEY || '';
  const emailPhoneEnabled = false;

  const inputPlaceholder = useMemo(
    () =>
      loginType === 'phone'
        ? 'Enter your phone number'
        : 'Enter your email address',
    [loginType]
  );

  const switchCtaLabel = useMemo(
    () =>
      loginType === 'phone' ? 'Login with Email' : 'Login with Phone Number',
    [loginType]
  );

  useEffect(() => {
    if (isAuthenticated) navigate(HOME);
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (loginStep !== 'otp') return;

    const interval = setInterval(() => {
      setSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [loginStep]);

  useEffect(() => {
    if (loginStep === 'otp') return;
    setRecaptchaToken(null);
    setOtpStepError('');
    recaptchaRef.current?.reset();
  }, [loginStep]);

  const handleSubmit = () => {
    const trimmedCredential = credential.trim();

    if (loginType === 'phone' && !isValidMobileNumber(trimmedCredential)) {
      setCredentialError('Please enter a valid phone number.');
      return;
    }

    if (loginType === 'email' && !isValidEmail(trimmedCredential)) {
      setCredentialError('Please enter a valid email address.');
      return;
    }

    setCredentialError('');
    setOtpStepError('');
    setRecaptchaToken(null);
    setLoginStep('otp');
    setSeconds(30);
  };

  const handleOtpNext = () => {
    setOtpStepError('');
    if (otp.length !== OTP_LENGTH) return;
    if (recaptchaSiteKey) {
      const captchaResponse =
        recaptchaToken ?? recaptchaRef.current?.getValue() ?? null;
      if (!captchaResponse) {
        setOtpStepError('Please complete the reCAPTCHA verification.');
        return;
      }
    }
    console.log('otp', otp);
    console.log('recaptchaToken', recaptchaToken);
    // setLoginStep('user-details');
  };

  const googleClientId = process.env.NX_GOOGLE_CLIENT_ID || '';

  const handleGoogleSignInSuccess = async (response: CredentialResponse) => {
    // TODO: Send response.credential to backend for verification/sign-in.
    console.log('Google sign-in success', response);
    if (response.credential) {
      const res = await googleOauthLoginApi({
        credential: response.credential,
      });
      if (res.user.id && res.token) {
        navigate(
          SSO_REDIRECT + `/schoolnet-cl?token=${res.token}&uid=${res.user.id}`
        );
      }
    }
  };

  const handleGoogleSignInError = () => {
    setCredentialError('Google sign-in failed. Please try again.');
  };

  const renderStepContent = () => {
    switch (loginStep) {
      case 'login':
        return (
          <>
            <Box sx={styles.containerTop}>
              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Typography variant="h2" fontWeight="regular">
                  Enter Your{' '}
                  <Typography
                    variant="h2"
                    component="span"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {loginType === 'phone' ? 'Phone Number' : 'Email Address'}
                  </Typography>
                </Typography>
              </Box>
              {emailPhoneEnabled && (
                <>
                  <Box sx={{ mb: { xs: pxToRem(15), md: pxTovW(30) } }}>
                    <InputFieldContainer
                      helperTextvariant="error"
                      topLabel={inputPlaceholder}
                      value={credential.trim()}
                      onChange={(event) => {
                        setCredential(event.target.value.trimStart());
                        setCredentialError('');
                      }}
                      onEnter={handleSubmit}
                      helperText={credentialError}
                    />
                  </Box>
                </>
              )}
              {loginType === 'email' && googleClientId && (
                <Box
                  sx={{ width: { xs: '100%', md: pxTovW(517) }, mx: 'auto' }}
                >
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <GoogleLogin
                        onSuccess={handleGoogleSignInSuccess}
                        onError={handleGoogleSignInError}
                        text="signin_with"
                        shape="pill"
                        width="320"
                      />
                    </Box>
                  </GoogleOAuthProvider>
                </Box>
              )}
            </Box>

            {emailPhoneEnabled && (
              <Box sx={styles.containerBottom}>
                <Box
                  sx={{
                    width: { xs: '100%', md: pxTovW(517) },
                    height: { xs: pxToRem(55), md: pxTovW(77) },
                  }}
                >
                  <PrimaryButton
                    fullWidth
                    disabled={credential.trim().length === 0}
                    onClick={handleSubmit}
                  >
                    Submit
                  </PrimaryButton>
                </Box>
                <Typography
                  variant="h4"
                  onClick={() => {
                    setLoginType((prev) =>
                      prev === 'phone' ? 'email' : 'phone'
                    );
                    setCredential('');
                    setCredentialError('');
                  }}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {switchCtaLabel}
                </Typography>
              </Box>
            )}
          </>
        );

      case 'otp':
        return (
          <>
            <Box sx={styles.containerTop}>
              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Typography variant="h2" fontWeight="regular">
                  Verify{' '}
                  <Typography
                    variant="h2"
                    component="span"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    {loginType === 'phone' ? 'Phone Number' : 'Email ID'}
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <InputFieldContainer
                  helperTextvariant="success"
                  topLabel={inputPlaceholder}
                  value={credential.trim()}
                  onChange={(event) =>
                    setCredential(event.target.value.trimStart())
                  }
                  onEnter={() => null}
                  helperText={`Otp sent to your ${
                    loginType === 'phone' ? 'phone number' : 'email'
                  }`}
                  nonEditable={true}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: pxToRem(25), md: pxTovW(22) },
                }}
              >
                <OtpInputs
                  enteredOtp={otp}
                  otpWrong={false}
                  setEnteredOtp={(value) => {
                    setOtp(value);
                    setOtpStepError('');
                  }}
                  otpLength={OTP_LENGTH}
                  onPressEnter={handleOtpNext}
                />
                {seconds === 0 ? (
                  <Box sx={{ display: 'flex', alignSelf: 'start' }}>
                    <LinkButton
                      dataId="resend-otp"
                      onClick={() => {
                        setSeconds(30);
                        setOtp('');
                        setRecaptchaToken(null);
                        setOtpStepError('');
                        recaptchaRef.current?.reset();
                      }}
                    >
                      Resend otp
                    </LinkButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignSelf: { xs: 'center', md: 'start' },
                      gap: { xs: pxToRem(4), md: pxTovW(4) },
                    }}
                  >
                    <Typography variant="cardText" sx={{ color: '#007CDC' }}>
                      Resend otp in
                    </Typography>
                    <Typography
                      sx={{ color: '#0AA34F' }}
                      variant="bodyText"
                      fontWeight="bold"
                    >
                      {seconds}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            {recaptchaSiteKey ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  my: { xs: pxToRem(20), md: pxTovW(28) },
                }}
              >
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaSiteKey}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => {
                    setRecaptchaToken(null);
                    recaptchaRef.current?.reset();
                  }}
                  onErrored={() => {
                    setRecaptchaToken(null);
                    setOtpStepError(
                      'reCAPTCHA could not load. Check your connection and try again.'
                    );
                  }}
                />
              </Box>
            ) : null}
            <Box sx={styles.containerBottom}>
              <Box
                sx={{
                  width: { xs: '100%', md: pxTovW(517) },
                  height: { xs: pxToRem(55), md: pxTovW(77) },
                }}
              >
                <PrimaryButton
                  fullWidth
                  disabled={otp.length !== OTP_LENGTH || !recaptchaToken}
                  onClick={handleOtpNext}
                >
                  Next
                </PrimaryButton>
              </Box>
              {otpStepError ? (
                <Typography
                  color="error"
                  variant="body2"
                  sx={{ textAlign: 'center' }}
                >
                  {otpStepError}
                </Typography>
              ) : null}
              <Typography
                variant="h4"
                onClick={() => {
                  setLoginStep('login');
                  setOtp('');
                  setOtpStepError('');
                  setRecaptchaToken(null);
                  recaptchaRef.current?.reset();
                }}
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Change {loginType === 'phone' ? 'Phone Number' : 'Email'}
              </Typography>
            </Box>
          </>
        );

      case 'user-details':
      default:
        return null;
    }
  };

  return <Box sx={styles.root}>{renderStepContent()}</Box>;
};

export default ClimateCourseLogin;
